import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { InternalGrid, ThemeDefault, Text } from '@paygreen/paygreen-ui';
import { useAPIError } from '../../../context';
import { IsBigDesktop } from '../../../utils/tools';
import { useNodeDimensions } from '../../../utils/hooks';
import { spaces } from '../../../utils/constants';
import { ErrorBoundary, Loader } from '../../../components';
import BillingTable from '../../user/billing/BillingTable';
import { getBilling } from '../../user/service';
import { OtherBillingCard } from '../shared';

const AdminUserSpaceBilling = ({
    t,
    space,
    organisationName,
    hasOtherSpaceBilling,
}) => {
    const { addError } = useAPIError();
    const fetchIdRef = useRef(0);
    const { accountId } = useParams();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [dataBilling, setDataBilling] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);

    // we create a ref using a special hook to get width from Main Internal Grid component
    const [ref, { width }] = useNodeDimensions();

    const fetchData = useCallback(
        ({ pageSize, pageIndex }) => {
            const fetchId = ++fetchIdRef.current;
            if (fetchId === fetchIdRef.current) {
                isMounted.current && setIsLoading(true);

                const params = {
                    limit: pageSize,
                    page: pageIndex + 1,
                };

                getBilling(accountId, space, params)
                    .then((res) => {
                        const { invoices, page_count } = res.data;

                        if (isMounted.current) {
                            setDataBilling(invoices);
                            setPageCount(page_count);
                        }
                    })
                    .catch((error) => {
                        const { status, detail } = error?.response?.data;

                        if (status === 404 || status === 422) {
                            isMounted.current && setDataBilling([]);
                        } else {
                            addError(detail, status);
                        }
                    })
                    .finally(() => {
                        isMounted.current && setIsLoading(false);
                    });
            }
        },
        [addError, accountId, space],
    );

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <>
            <Text htmlTag="strong" colorWab="grey50" textSize="md">
                {t(`admin.billing.${space}.title`)}
            </Text>

            <div ref={ref}>
                <InternalGrid
                    childrenMarginTop="md"
                    hasStaticWidth={true}
                    displayType={IsBigDesktop() ? 'flex' : 'grid'}
                    gridGap="md"
                    gridTemplateColumns="1fr"
                    justifyItems="stretch"
                >
                    <ErrorBoundary>
                        <BillingTable
                            accountId={accountId}
                            data={dataBilling}
                            fetchData={fetchData}
                            loading={isLoading}
                            pageCount={pageCount}
                            setIsDownloading={setIsDownloading}
                        />
                    </ErrorBoundary>

                    {hasOtherSpaceBilling ? (
                        <OtherBillingCard
                            blockWidth={
                                width < ThemeDefault.breakpoint.lg
                                    ? 'none'
                                    : 'md'
                            }
                            isLoading={isLoading}
                            space={
                                space === spaces.charity
                                    ? spaces.climate
                                    : spaces.charity
                            }
                            accountId={accountId}
                            organisationName={organisationName}
                        />
                    ) : null}
                </InternalGrid>
            </div>

            {isDownloading && <Loader isLoading={isDownloading} />}
        </>
    );
};

AdminUserSpaceBilling.propTypes = {
    space: PropTypes.oneOf(Object.values(spaces)).isRequired,
    organisationName: PropTypes.string.isRequired,
    hasOtherSpaceBilling: PropTypes.bool.isRequired,
};

export default withTranslation('common')(AdminUserSpaceBilling);
